/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import appTheme from '../theme/appTheme.json';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useField } from 'formik';
import { BeamInputLabel, BeamInputLabelProps } from './BeamInputLabel';
import React from 'react';
import { Box, FormHelperText } from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { BeamHelpText } from "./BeamHelpText";
import { isNil } from "lodash";

import { AutofillWrapper } from "./AutofillWrapper";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

export interface BeamDatePickerProps {
  name: string;
  helpText?: string;
  disableInput?: boolean;
}

export const BeamDatePicker = ({
  name,
  helpText,
  disableInput,
  label,
  optional,
  required,
}: BeamDatePickerProps & Omit<BeamInputLabelProps, 'htmlFor'>) => {
  const [field, meta, helpers] = useField<string>(name);

  return (
    <AutofillWrapper>
      <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
        <BeamInputLabel label={label} optional={optional} required={required} htmlFor={name}/>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDatePicker
            disabled={disableInput}
            format="YYYY/MM/DD"
            value={moment.utc(field.value)}
            onChange={(date: moment.Moment | null) => {
              if (date === null) return;
              helpers.setValue(moment.utc(date.format('YYYY-MM-DD')).toISOString());
            }}
            slotProps={{
              popper: {
                sx: {
                  '.MuiPickersDay-root': {
                    backgroundColor: appTheme.primaryBackgroundColor,
                    color: appTheme.primaryTextColor,
                    '&:hover': {
                      color: appTheme.primaryTextColor,
                      backgroundColor: `${appTheme.mptSelectIconGray}`,
                    },
                  },
                  '.MuiDayCalendar-weekDayLabel': {
                    color: appTheme.secondaryTextColor,
                  },
                  '.Mui-selected': {
                    color: appTheme.primaryBackgroundColor,
                    backgroundColor: `${appTheme.mptLightBlue} !important`,
                    '&:hover': {
                      color: `${appTheme.primaryTextColor} !important`,
                      backgroundColor: `${appTheme.mptSelectIconGray} !important`,
                    },
                  },
                },
              },
              textField: {
                id: name,
                onBlur: () => {
                  helpers.setTouched(true);
                },
                sx: {
                  input: {
                    color: appTheme.primaryTextColor,
                    height: '8px',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: isNil(meta.error) || !meta.touched ? '#00C2DE' : '#FA7272',
                    },
                    '&.Mui-disabled fieldset': {
                      borderColor: '#677A89 !important',
                    },
                    '&.Mui-disabled input': {
                      color: '#677A89 !important',
                      "-webkit-text-fill-color": '#677A89 !important',
                    },
                    '&:hover fieldset': {
                      borderColor: 'white',
                    },
                    '& fieldset': {
                      borderColor: isNil(meta.error) || !meta.touched ? '#9FAFBC' : '#FA7272',
                    },
                  },
                  fieldset: {
                    borderColor: appTheme.primaryTextColor,
                  },
                },
              },

            }}
          />
          {!isNil(meta.error) && meta.touched && (
            <FormHelperText sx={{marginLeft: '15px'}} error={!isNil(meta.error)}>
              {meta.error}
            </FormHelperText>
          )}
        </LocalizationProvider>
        <BeamHelpText helpText={helpText}/>
      </Box>
    </AutofillWrapper>
  );
};
